import axios, { type AxiosRequestConfig } from 'axios';
import type { Counterparty, CounterpartyTeam, WTRequest, LoginRequest, LoginResponse, DashboardStatsResponse } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const AUTH_TOKEN_KEY = 'airex_cp_token';
const TRANSIENT_RETRY_STATUSES = new Set([429, 500, 502, 503, 504]);
const RETRYABLE_METHODS = new Set(['get', 'head', 'options']);
const MAX_TRANSIENT_RETRIES = 2;

function readCookieToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${AUTH_TOKEN_KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookieToken(token: string): void {
  if (typeof document === 'undefined') return;
  // 30 дней, same-site для стабильности в мобильных webview.
  document.cookie = `${AUTH_TOKEN_KEY}=${encodeURIComponent(token)}; Max-Age=${60 * 60 * 24 * 30}; Path=/; SameSite=Lax`;
}

function clearCookieToken(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${AUTH_TOKEN_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function getAuthToken(): string | null {
  let local: string | null = null;
  let session: string | null = null;
  try { local = localStorage.getItem(AUTH_TOKEN_KEY); } catch { /* no-op */ }
  try { session = sessionStorage.getItem(AUTH_TOKEN_KEY); } catch { /* no-op */ }
  const cookie = readCookieToken();
  const token = local || session || cookie;
  if (!token) return null;
  // Самовосстановление хранилищ после mobile reload/orientation.
  try {
    if (!local) localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch { /* no-op */ }
  try {
    if (!session) sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch { /* no-op */ }
  if (!cookie) writeCookieToken(token);
  return token;
}

export function setAuthToken(token: string): void {
  try { localStorage.setItem(AUTH_TOKEN_KEY, token); } catch { /* no-op */ }
  try { sessionStorage.setItem(AUTH_TOKEN_KEY, token); } catch { /* no-op */ }
  writeCookieToken(token);
}

export function clearAuthToken(): void {
  try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch { /* no-op */ }
  try { sessionStorage.removeItem(AUTH_TOKEN_KEY); } catch { /* no-op */ }
  clearCookieToken();
}

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

type RetryableConfig = AxiosRequestConfig & {
  __transientRetryCount?: number;
  __authProbeDone?: boolean;
  __skipAuthInterceptor?: boolean;
};

function shouldRetryTransientError(err: unknown): boolean {
  const axiosErr = err as {
    code?: string;
    response?: { status?: number };
    config?: { method?: string } & RetryableConfig;
  };
  const method = String(axiosErr.config?.method || 'get').toLowerCase();
  const retries = axiosErr.config?.__transientRetryCount ?? 0;
  if (retries >= MAX_TRANSIENT_RETRIES) return false;

  if (!RETRYABLE_METHODS.has(method)) return false;

  const status = axiosErr.response?.status;
  if (status && TRANSIENT_RETRY_STATUSES.has(status)) return true;

  // Таймаут/разрыв сети/временная недоступность апстрима без HTTP-статуса.
  const code = String(axiosErr.code || '');
  return code === 'ECONNABORTED' || code === 'ERR_NETWORK';
}

function getRetryDelayMs(retryCount: number): number {
  // 300ms, 700ms — короткий backoff, чтобы пережить краткий рестарт апстрима.
  return 300 + retryCount * 400;
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Для FormData не задаём Content-Type — axios сам выставит multipart/form-data с boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = (err.config ?? {}) as RetryableConfig & { method?: string; url?: string };
    const requestUrl = String(cfg.url || '');

    if (shouldRetryTransientError(err)) {
      const nextRetry = (cfg.__transientRetryCount ?? 0) + 1;
      cfg.__transientRetryCount = nextRetry;
      await new Promise((resolve) => setTimeout(resolve, getRetryDelayMs(nextRetry)));
      return api.request(cfg);
    }

    const status = err.response?.status;
    const isLoginRequest =
      requestUrl.includes('/counterparty/login') ||
      requestUrl.includes('/counterparty/login/2fa') ||
      requestUrl === '/login';

    // На мобильных и при кратких сбоях возможен единичный ложный 401.
    // Перед разлогином делаем один auth-probe запрос профиля.
    if (
      status === 401 &&
      !window.location.pathname.includes('/login') &&
      !isLoginRequest &&
      !cfg.__skipAuthInterceptor &&
      !cfg.__authProbeDone
    ) {
      cfg.__authProbeDone = true;
      try {
        const token = getAuthToken();
        if (!token) throw new Error('missing token');
        await api.get('/counterparty/profile', {
          timeout: 10000,
          __skipAuthInterceptor: true,
        } as RetryableConfig);
        return api.request(cfg);
      } catch {
        clearAuthToken();
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    if (status === 401 && !window.location.pathname.includes('/login') && !isLoginRequest && !cfg.__skipAuthInterceptor) {
      clearAuthToken();
      window.location.href = '/login';
    }
    if (status === 403 && !window.location.pathname.includes('/login') && !isLoginRequest && !cfg.__skipAuthInterceptor) {
      clearAuthToken();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const cpApi = {
  login: async (creds: LoginRequest): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/counterparty/login', { ...creds, platform: 'airex' });
    return data;
  },

  loginWith2FA: async (login: string, code: string): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/counterparty/login/2fa', { login, code, platform: 'airex' });
    return data;
  },

  getProfile: async (): Promise<Counterparty> => {
    const { data } = await api.get<{ data: Counterparty }>('/counterparty/profile');
    return data.data || data;
  },

  updateProfile: async (payload: { login?: string; password?: string }) => {
    await api.put('/counterparty/profile', payload);
  },

  updateTeamMemberProfile: async (counterpartyId: number, payload: { login?: string; password?: string; show_rates_in_sidebar?: boolean }) => {
    await api.put(`/counterparty/team-members/${counterpartyId}/profile`, payload);
  },

  getMyTeam: async (): Promise<CounterpartyTeam | null> => {
    const { data } = await api.get<{ data: CounterpartyTeam | null }>('/counterparty/my-team');
    return data.data ?? null;
  },

  addMyTeamMember: async (payload: { name: string; login: string; password: string }) => {
    const { data } = await api.post<{ data: unknown }>('/counterparty/my-team/members', payload);
    return data;
  },

  removeMyTeamMember: async (counterpartyId: number) => {
    await api.delete(`/counterparty/my-team/members/${counterpartyId}`);
  },

  getSalaryBalance: async (): Promise<number> => {
    const { data } = await api.get<{ data?: { salary_balance?: number } }>('/counterparty/salary-balance');
    return data?.data?.salary_balance ?? 0;
  },

  getDashboardStats: async (params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const { data } = await api.get<DashboardStatsResponse>(`/counterparty/dashboard-stats${q.toString() ? `?${q}` : ''}`);
    return data;
  },

  getRequests: async (params?: { status?: string; date_from?: string; date_to?: string }): Promise<WTRequest[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/counterparty/requests${q.toString() ? `?${q}` : ''}`);
    const arr = data.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  getAvailableAirexRequests: async (params?: { amount?: number; tolerance_percent?: number; limit?: number }): Promise<unknown[]> => {
    const q = new URLSearchParams();
    if (params?.amount != null && params.amount > 0) q.set('amount', String(params.amount));
    if (params?.tolerance_percent != null && params.tolerance_percent > 0) q.set('tolerance_percent', String(params.tolerance_percent));
    if (params?.limit != null && params.limit > 0) q.set('limit', String(params.limit));
    const url = `/airex/requests/available${q.toString() ? `?${q}` : ''}`;
    const { data } = await api.get(url, { timeout: 30000 });
    const arr = data.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  /** Заявки с остатком >= amount (те же фильтры что у «доступных»), без ±% по сумме */
  postMatchCandidates: async (params: {
    amount: number;
    direction_code?: string;
    bank_name?: string;
    limit?: number;
  }): Promise<Array<{
    id: number;
    order_id: string;
    amount: number;
    payed_amount?: number;
    payed_amount_counterparty?: number;
    remaining_rub: number;
    bank_name?: string;
    direction_code?: string | null;
    card_holder?: string;
    exchange_rate?: number;
    status?: string;
    deadline_at?: string | null;
    max_matches?: number;
  }>> => {
    const lim = params.limit != null && params.limit > 0 ? Math.min(50, params.limit) : 30;
    const { data } = await api.post(
      '/airex/requests/match-candidates',
      {
        amount: params.amount,
        direction_code: params.direction_code || '',
        bank_name: params.bank_name || '',
        limit: lim,
      },
      { timeout: 30000 }
    );
    const arr = data.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  getMyAirexRequests: async (params?: { dateFrom?: string; dateTo?: string; status?: string; search?: string; limit?: number; offset?: number }): Promise<{ data: unknown[]; total_count: number }> => {
    const q = new URLSearchParams();
    if (params?.dateFrom) q.set('date_from', params.dateFrom);
    if (params?.dateTo) q.set('date_to', params.dateTo);
    if (params?.status) q.set('status', params.status);
    if (params?.search?.trim()) q.set('search', params.search.trim());
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const { data } = await api.get(`/airex/requests/my${q.toString() ? `?${q}` : ''}`, { timeout: 30000 });
    return { data: Array.isArray(data.data) ? data.data : [], total_count: data.total_count ?? 0 };
  },

  getAirexRequestMatches: async (requestId: number) => {
    const { data } = await api.get(`/airex/requests/${requestId}/matches`);
    return (data?.data?.list ?? data?.list ?? []) as unknown[];
  },

  getAirexRequestMatchesBatch: async (requestIds: number[]) => {
    const ids = requestIds.filter((x) => Number.isFinite(x) && x > 0);
    if (!ids.length) return {} as Record<number, unknown[]>;
    const { data } = await api.post(`/airex/requests/matches-batch`, { ids }, { timeout: 30000 });
    const raw = (data?.data?.by_request_id ?? {}) as Record<string, unknown[]>;
    const out: Record<number, unknown[]> = {};
    for (const [k, v] of Object.entries(raw)) {
      const id = parseInt(k, 10);
      if (!Number.isNaN(id)) out[id] = Array.isArray(v) ? v : [];
    }
    return out;
  },

  getAirexClosureRate: async (requestId: number, counterpartyId: number): Promise<number> => {
    const { data } = await api.get<{ exchange_rate: number }>(
      `/airex/requests/${requestId}/closure-rate`,
      { params: { counterparty_id: counterpartyId } }
    );
    return Number(data?.exchange_rate || 0);
  },

  getAirexClosureRatesBatch: async (requestIds: number[], counterpartyId: number): Promise<Record<number, number>> => {
    const ids = requestIds.filter((x) => Number.isFinite(x) && x > 0);
    if (!ids.length) return {};
    const { data } = await api.post(
      `/airex/requests/closure-rate-batch`,
      { ids },
      { params: { counterparty_id: counterpartyId }, timeout: 30000 }
    );
    const raw = (data?.data?.by_request_id ?? {}) as Record<string, unknown>;
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      const id = parseInt(k, 10);
      const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : 0;
      if (!Number.isNaN(id) && Number.isFinite(n)) out[id] = n;
    }
    return out;
  },

  getMatchSettings: async () => {
    const { data } = await api.get('/airex-settings/match-settings');
    return data;
  },

  getBankNamesMappings: async (): Promise<Array<{ id: number; canonical_name?: string; interpretations?: string[] }>> => {
    const { data } = await api.get('/bank-names-mapping');
    const arr = data?.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  getWallets: async (params?: { limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const { data } = await api.get(`/wallets${q.toString() ? `?${q}` : ''}`);
    return { wallets: data.data ?? [], total: data.total ?? 0 };
  },

  createManualMatch: async (requestId: number, formData: FormData) => {
    const { data } = await api.post(`/airex/requests/${requestId}/manual-match`, formData, {
      timeout: 60000,
    });
    return data;
  },

  pickRequest: async (params: { amount: number; direction_code?: string; bank_name?: string; timeout_seconds?: number }) => {
    const timeout = Math.max(0, Math.min(300, params.timeout_seconds ?? 0));
    // минимум 120 сек — при 200–300 заявках обработка занимает значительное время
    const waitMs = Math.max((timeout + 60) * 1000, 120000);
    const { data } = await api.post(
      '/airex/requests/pick',
      {
        amount: params.amount,
        direction_code: params.direction_code || '',
        bank_name: params.bank_name || '',
        timeout_seconds: timeout,
      },
      { timeout: waitMs }
    );
    return data;
  },

  getWTRequest: async (id: number) => {
    const { data } = await api.get(`/wt-requests/${id}`);
    return data?.data ?? data;
  },

  downloadReceipt: async (wtRequestId: number): Promise<Blob> => {
    const { data } = await api.get(`/wt-requests/${wtRequestId}/download-receipt`, { responseType: 'blob' });
    return data as Blob;
  },

  updateReceiptCheckFlags: async (requestId: number, flags: { check_amount?: boolean; check_recipient?: boolean; check_card?: boolean; check_bank?: boolean; check_status?: boolean }) => {
    await api.post(`/wt-requests/${requestId}/receipt-check-flags`, flags);
  },

  resetReceiptOriginality: async (requestId: number) => {
    await api.post(`/wt-requests/${requestId}/receipt-originality/reset`);
  },

  replaceReceipt: async (requestId: number, file: File): Promise<{ file_path: string }> => {
    const formData = new FormData();
    formData.append('receipt', file);
    const { data } = await api.post<{ file_path: string }>(`/wt-requests/${requestId}/replace-receipt`, formData);
    return data;
  },

  takeRequest: async (id: number) => api.post(`/counterparty/requests/${id}/take`, undefined, { timeout: 45000 }),
  takeAirexRequest: async (id: number, opts?: { amount?: number; expectedRemainingRub?: number }) => {
    const body: Record<string, number> = {};
    if (opts?.amount != null && Number.isFinite(opts.amount) && opts.amount > 0) body.amount = opts.amount;
    if (opts?.expectedRemainingRub != null && Number.isFinite(opts.expectedRemainingRub)) {
      body.expected_remaining_rub = opts.expectedRemainingRub;
    }
    const { data } = await api.post(`/airex/requests/${id}/take`, Object.keys(body).length ? body : {}, { timeout: 45000 });
    return data;
  },
  confirmRequest: async (id: number) => api.post(`/counterparty/requests/${id}/confirm`),
  rejectRequest: async (id: number, comment?: string) => api.post(`/counterparty/requests/${id}/reject`, { comment: comment || '' }),
  /** Отменить WT-заявку и мэтч (требует can_cancel у контрагента), rejection_method=contr */
  rejectWTRequest: async (wtRequestId: number) => api.post(`/wt-requests/${wtRequestId}/reject?method=contr`),

  updateActivity: async () => api.post('/counterparty/activity'),

  getProfitHistory: async (params?: { from?: string; to?: string; trader_id?: number }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    if (params?.trader_id != null) q.set('trader_id', String(params.trader_id));
    const { data } = await api.get(`/counterparty/profit-history${q.toString() ? `?${q}` : ''}`);
    return data;
  },

  getMyCurrentRates: async () => {
    const { data } = await api.get('/counterparty/current-rates');
    return data as {
      calculated_commissions: { min: number; max: number; rate: number }[];
      base_rate?: number;
      commissions?: { min: number; max: number; percent: number }[];
      currency_commissions?: Record<string, { min: number; max: number; percent: number }[]>;
      currency_rates?: Record<string, {
        base_rate?: number;
        commissions?: { min: number; max: number; percent: number }[];
        calculated_commissions: { min: number; max: number; rate: number }[];
      }>;
    };
  },

  getTransactions: async (params?: { limit?: number; offset?: number; to_entity_type?: string; to_entity_id?: number; date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.to_entity_type) q.set('to_entity_type', params.to_entity_type);
    if (params?.to_entity_id != null) q.set('to_entity_id', String(params.to_entity_id));
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/transactions${q.toString() ? `?${q}` : ''}`);
    return { transactions: data.data ?? [], total: data.total ?? 0 };
  },

  transferTeamInternal: async (payload: { from_counterparty_id: number; to_counterparty_id: number; amount: number; comment?: string }) => {
    const { data } = await api.post('/counterparty/team-transfer', payload);
    return data;
  },

  createWithdrawTask: async (payload: { amount: number; network: 'HTX_UID' | 'BYBIT_UID' | 'BEP20' | 'TRC20' | 'TON' | 'ERC20'; wallet?: string; htx_uid?: string; comment?: string }) => {
    const { data } = await api.post('/counterparty/withdraw-tasks', payload);
    return data;
  },

  getTeamTransactions: async (params?: { limit?: number; offset?: number; type?: string; member_id?: number; date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.type) q.set('type', params.type);
    if (params?.member_id != null) q.set('member_id', String(params.member_id));
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/counterparty/team-transactions${q.toString() ? `?${q}` : ''}`);
    return { transactions: data.data ?? [], total: data.total ?? 0 };
  },

  setup2FA: async () => (await api.post('/counterparty/2fa/setup')).data,
  enable2FA: async (code: string) => (await api.post('/counterparty/2fa/enable', { code })).data,
  disable2FA: async (code: string) => (await api.post('/counterparty/2fa/disable', { code })).data,
  get2FAQRCode: async () => (await api.get('/counterparty/2fa/qr-code')).data,
  getBackupCodes: async () => (await api.post('/counterparty/2fa/backup-codes')).data,
};

export default api;
